// Netzwerk-Topologie-Graph (Internet → Gateway → Hosts) mit Cytoscape.
// Die Bibliothek wird per dynamic import erst geladen, wenn die Seite
// tatsächlich einen Graphen enthält — alle anderen Seiten zahlen nichts.
// Initialisierung läuft über das up:fragment:inserted-DOM-Event (kein
// up.compiler, die schlagen in diesem Projekt still fehl) plus einen
// Direktaufruf für die initial gerenderte Seite.

async function initNetworkGraphs(root) {
  const scope = root instanceof Element ? root : document.body
  if (!scope) return
  const containers = Array.from(scope.querySelectorAll('.network-graph'))
  if (scope.matches?.('.network-graph')) containers.unshift(scope)
  const fresh = containers.filter((el) => !el.dataset.graphInitialized)
  if (fresh.length === 0) return

  const { default: cytoscape } = await import('cytoscape')
  fresh.forEach((el) => initGraph(cytoscape, el))
}

// Lucide-SVGs nutzen stroke="currentColor"; als background-image gibt es
// keinen CSS-Kontext mehr, daher wird die Textfarbe des Containers eingebrannt.
function iconDataUri(svg, color) {
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg.replaceAll('currentColor', color))
}

// Gateway im Zentrum, Hosts gleichmäßig auf einem Kreis darum, Internet-Wolke
// oben außerhalb. Richtung Internet bleibt ein Sektor des Kreises frei, damit
// die Internet-Kante nicht durch Host-Knoten läuft. Der Radius wächst mit der
// Hostzahl, damit die Labels nebeneinander Platz haben.
function circularPositions(graph) {
  const hosts = graph.nodes.filter((node) => node.id !== 'internet' && node.id !== 'gateway')
  const radius = Math.max(150, (hosts.length * 110) / (2 * Math.PI))
  const positions = {
    gateway: { x: 0, y: 0 },
    internet: { x: 0, y: -(radius + 130) },
  }
  const gapCenter = -Math.PI / 2
  const gap = hosts.length > 2 ? Math.PI / 5 : 0
  const span = 2 * Math.PI - gap
  hosts.forEach((node, i) => {
    const angle = gapCenter + gap / 2 + span * (i + 0.5) / hosts.length
    positions[node.id] = { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius }
  })
  return positions
}

function initGraph(cytoscape, el) {
  el.dataset.graphInitialized = 'true'
  const graph = JSON.parse(el.dataset.graph)
  const styles = getComputedStyle(el)
  const ink = styles.color
  const line = styles.getPropertyValue('--graph-line').trim() || ink

  const elements = [
    ...graph.nodes.map((node) => {
      // Hosts tragen ihre Geschäftskritikalitätsfarbe (grün→rot); Internet und
      // ein Gateway ohne Host-Datensatz bleiben in der neutralen Textfarbe.
      const tint = node.color || ink
      return {
        data: {
          id: node.id,
          label: node.label + (node.sublabel ? '\n' + node.sublabel : ''),
          icon: iconDataUri(node.icon, tint),
          url: node.url,
          ring: node.color || 'transparent',
          ringWidth: node.color ? 3 : 0,
        },
      }
    }),
    ...graph.edges.map((edge) => ({ data: { source: edge.source, target: edge.target } })),
  ]

  const cy = cytoscape({
    container: el,
    elements,
    minZoom: 0.2,
    maxZoom: 3,
    autoungrabify: true,
    boxSelectionEnabled: false,
    style: [
      {
        selector: 'node',
        style: {
          'shape': 'ellipse',
          'background-color': 'data(ring)',
          'background-opacity': 0.12,
          'border-width': 'data(ringWidth)',
          'border-color': 'data(ring)',
          'width': 40,
          'height': 40,
          'padding': 6,
          'background-image': 'data(icon)',
          'background-fit': 'contain',
          'background-clip': 'none',
          'label': 'data(label)',
          'text-wrap': 'wrap',
          'text-max-width': '160px',
          'text-valign': 'bottom',
          'text-halign': 'center',
          'text-margin-y': 6,
          'font-size': '11px',
          'font-family': styles.fontFamily,
          'color': ink,
        },
      },
      {
        selector: 'edge',
        style: {
          'width': 1.5,
          'line-color': line,
          'curve-style': 'straight',
        },
      },
    ],
    layout: {
      name: 'preset',
      positions: circularPositions(graph),
      fit: true,
      padding: 24,
    },
  })

  // Instanz für Tests/Debugging am Container verfügbar machen.
  el.cytoscape = cy

  cy.on('tap', 'node', (event) => {
    const url = event.target.data('url')
    if (!url) return
    if (window.up) {
      up.navigate({ url })
    } else {
      window.location.assign(url)
    }
  })
  cy.on('mouseover', 'node', (event) => {
    if (event.target.data('url')) el.style.cursor = 'pointer'
  })
  cy.on('mouseout', 'node', () => { el.style.cursor = '' })
}

document.addEventListener('up:fragment:inserted', (event) => initNetworkGraphs(event.target))
initNetworkGraphs(document.body)
